import { csvFormatBody, csvParse, tsvFormatBody, tsvParse } from "d3-dsv";
// Inline Split type — replaces ts-toolbelt dep so consumers don't need it
type Split<S extends string, D extends string> = string extends S
  ? string[]
  : S extends `${infer T}${D}${infer U}`
    ? [T, ...Split<U, D>]
    : [S];
import { lines } from "../../lines";
import { maps } from "../../maps";
import { skips } from "../../skips";
import { throughs } from "../../throughs";

export function csvFormats<S extends string>(
  header: S,
): TransformStream<Record<Split<S, ",">[number], string>, string>;
export function csvFormats<S extends string[]>(
  header: S,
): TransformStream<Record<S[number], string>, string>;
export function csvFormats(
  header: string | string[],
): TransformStream<Record<string, string>, string> {
  const _header = typeof header === "string" ? header.split(",") : header;
  return new TransformStream({
    start: (ctrl) => ctrl.enqueue(`${_header.join(",")}\n`),
    transform: (chunk, ctrl) =>
      ctrl.enqueue(`${csvFormatBody([chunk], _header)}\n`),
  });
}

export function csvParses<S extends string>(
  header: S,
): TransformStream<string, Record<Split<S, ",">[number], string>>;
export function csvParses<S extends string[]>(
  header: S,
): TransformStream<string, Record<S[number], string>>;
export function csvParses(
  header: string | string[],
): TransformStream<string, Record<string, string>> {
  const _header = typeof header === "string" ? header.split(",") : header;
  const _i = 0;
  return throughs<string, Record<string, string>>((r) =>
    r
      .pipeThrough(lines({ EOL: "LF" }))
      .pipeThrough(skips(1))
      .pipeThrough(maps((line) => csvParse(`${_header}\n${line}`)[0])),
  );
}

export function tsvFormats<S extends string>(
  header: S,
): TransformStream<Record<Split<S, "\t">[number], string>, string>;
export function tsvFormats<S extends string[]>(
  header: S,
): TransformStream<Record<S[number], string>, string>;
export function tsvFormats(
  header: string | string[],
): TransformStream<Record<string, string>, string> {
  const sep = "\t";
  const _header = typeof header === "string" ? header.split(sep) : header;
  return new TransformStream({
    start: (ctrl) => ctrl.enqueue(`${_header.join(sep)}\n`),
    transform: (chunk, ctrl) =>
      ctrl.enqueue(`${tsvFormatBody([chunk], _header)}\n`),
  });
}

export function tsvParses<S extends string>(
  header: S,
): TransformStream<string, Record<Split<S, "\t">[number], string>>;
export function tsvParses<S extends string[]>(
  header: S,
): TransformStream<string, Record<S[number], string>>;
export function tsvParses(
  header: string | string[],
): TransformStream<string, Record<string, string>> {
  const _header = typeof header === "string" ? header.split("\t") : header;
  const _i = 0;
  return throughs<string, Record<string, string>>((r) =>
    r
      .pipeThrough(lines({ EOL: "LF" }))
      .pipeThrough(skips(1))
      .pipeThrough(maps((line) => tsvParse(`${_header}\n${line}`)[0])),
  );
}
